import {FC, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button, Icon, Menu, Modal, Table} from "semantic-ui-react";
import {LoadingMessage, ErrorMessage} from "@/components/SystemMessages";
import {
    DataStatus,
    IColumnMetadata,
    ICustomColumnsTransformation,
    IDataTableData
} from "@/features/application/api/applicationModels";
import dataFormatUtil, {DataType} from "@/util/dataFormatUtil";
import dataTableUtil from "@/components/DataTable/dataTableUtils";
import {DataTable} from "@/components/DataTable";
import {AppState} from "@/state/rootReducer";
import {
    ISosBusinessImpactHourlyData,
    ISosBusinessImpactResponse
} from "../api/coachInsightsModels";
import coachInsightsAPI from "../api/coachInsightsAPI";
import {ISODateString} from "@/util/dateUtil";
import {formatDuration, formatHourRange} from "@/features/coachInsights/api/coachInsightsUtils";

enum ComparisonMode {
    Threshold = "threshold",
    Wow = "wow",
    Yoy = "yoy"
}

interface IProps {
    open: boolean;
    onClose: () => void;
    locationPk: number;
    businessDate: ISODateString;
    locationName: string;
    timeframeStartHour: number;
    timeframeEndHour: number;
}

interface IMetricCardProps {
    label: string;
    actualSeconds: number;
    thresholdSeconds: number;
    comparisonMode: ComparisonMode;
    comparisonSeconds?: number;
}

const MetricCard: FC<IMetricCardProps> = ({
    label,
    actualSeconds,
    thresholdSeconds,
    comparisonMode,
    comparisonSeconds
}) => {
    let deltaSeconds: number;
    let deltaLabel: string;
    let subLabel: string;

    if (comparisonMode === ComparisonMode.Threshold) {
        deltaSeconds = actualSeconds - thresholdSeconds;
        deltaLabel = "Over threshold";
        subLabel =
            `${formatDuration(actualSeconds)} actual vs ` +
            `${formatDuration(thresholdSeconds)} target`;
    } else {
        const compSec = comparisonSeconds ?? 0;
        deltaSeconds = actualSeconds - compSec;
        deltaLabel =
            comparisonMode === ComparisonMode.Wow ? "vs last week" : "vs last year";
        subLabel =
            `${formatDuration(actualSeconds)} actual vs ` +
            `${formatDuration(compSec)} ` +
            (comparisonMode === ComparisonMode.Wow ? "last week" : "last year");
    }

    const isOver = deltaSeconds > 0;
    const formatted = (isOver ? "+" : "") + formatDuration(Math.abs(deltaSeconds));

    return (
        <div className={`business-impact-metric-card ${isOver ? "over" : "under"}`}>
            <div className="business-impact-metric-card-label">{label}</div>
            <div className="business-impact-metric-card-delta">
                {isOver && <Icon name="arrow up" className="delta-arrow" />}
                {!isOver && deltaSeconds < 0 && (
                    <Icon name="arrow down" className="delta-arrow" />
                )}
                <span className="delta-value">{formatted}</span>
            </div>
            <div className="business-impact-metric-card-sublabel">{deltaLabel}</div>
            <div className="business-impact-metric-card-detail">{subLabel}</div>
        </div>
    );
};

const staticColumns: IColumnMetadata[] = [
    {
        key: "hour",
        label: "Hour",
        dataType: DataType.String,
        decimalPlaces: 0,
        hasFilter: true,
        isToggleable: false,
        isToggledToVisible: true,
        tooltipText: "",
        unit: ""
    },
    {
        key: "driveThruSales",
        label: "DT Sales",
        dataType: DataType.Money,
        decimalPlaces: 2,
        hasFilter: false,
        isToggleable: false,
        isToggledToVisible: true,
        tooltipText: "",
        unit: "$"
    },
    {
        key: "driveThruTransactions",
        label: "DT Trans",
        dataType: DataType.Number,
        decimalPlaces: 0,
        hasFilter: false,
        isToggleable: false,
        isToggledToVisible: true,
        tooltipText: "",
        unit: ""
    },
    {
        key: "carsOverThreshold",
        label: "Cars Over Threshold",
        dataType: DataType.Number,
        decimalPlaces: 0,
        hasFilter: false,
        isToggleable: false,
        isToggledToVisible: true,
        tooltipText: "",
        unit: ""
    }
];

const configurableColumns: IColumnMetadata[] = [
    {
        key: "total",
        label: "DT SWS Total Time",
        dataType: DataType.TimeSpan,
        decimalPlaces: 0,
        hasFilter: false,
        isToggleable: false,
        isToggledToVisible: true,
        tooltipText: "",
        unit: ""
    },
    {
        key: "menu",
        label: "Menu Time",
        dataType: DataType.TimeSpan,
        decimalPlaces: 0,
        hasFilter: false,
        isToggleable: false,
        isToggledToVisible: true,
        tooltipText: "",
        unit: ""
    },
    {
        key: "queue",
        label: "Queue Time",
        dataType: DataType.TimeSpan,
        decimalPlaces: 0,
        hasFilter: false,
        isToggleable: false,
        isToggledToVisible: true,
        tooltipText: "",
        unit: ""
    },
    {
        key: "service",
        label: "Window Time",
        dataType: DataType.TimeSpan,
        decimalPlaces: 0,
        hasFilter: false,
        isToggleable: false,
        isToggledToVisible: true,
        tooltipText: "",
        unit: ""
    }
];

function buildCustomColumns(
    thresholdSeconds: number,
    menuThresholdSeconds: number,
    queueThresholdSeconds: number,
    serviceThresholdSeconds: number,
    columns: IColumnMetadata[]
): ICustomColumnsTransformation {
    const thresholdMap: Record<string, number> = {
        total: thresholdSeconds,
        menu: menuThresholdSeconds,
        queue: queueThresholdSeconds,
        service: serviceThresholdSeconds
    };

    function timingCell(key: string) {
        const colDef = columns.find((c) => c.key === key);
        const threshold = thresholdMap[key] ?? 0;
        return {
            getCustomColumnMetaData: (row: IDataTableData) => {
                const val = Number(row[key]);
                const isOver = val > threshold;
                return {
                    element: (
                        <Table.Cell
                            key={key}
                            className={isOver ? "over-threshold" : ""}
                        >
                            {row[key] != null && colDef
                                ? dataFormatUtil.dataTableDisplayValue(val, colDef)
                                : ""}
                        </Table.Cell>
                    ),
                    sortValue: row[key]
                };
            }
        };
    }

    function carsCell() {
        return {
            getCustomColumnMetaData: (row: IDataTableData) => {
                const val = Number(row.carsOverThreshold);
                return {
                    element: (
                        <Table.Cell
                            key="carsOverThreshold"
                            className={val > 0 ? "over-threshold" : ""}
                        >
                            {val}
                        </Table.Cell>
                    ),
                    sortValue: row.carsOverThreshold
                };
            }
        };
    }

    return {
        carsOverThreshold: carsCell(),
        total: timingCell("total"),
        menu: timingCell("menu"),
        queue: timingCell("queue"),
        service: timingCell("service")
    };
}

function computeTotalRow(
    rows: ISosBusinessImpactHourlyData[]
): ISosBusinessImpactHourlyData | null {
    if (rows.length === 0) return null;
    const totalTrans = rows.reduce((s, r) => s + r.driveThruTransactions, 0);
    return {
        hour: -1,
        driveThruSales: rows.reduce((s, r) => s + r.driveThruSales, 0),
        driveThruTransactions: totalTrans,
        carsOverThreshold: rows.reduce((s, r) => s + r.carsOverThreshold, 0),
        driveThruTotalTimeSec:
            totalTrans > 0
                ? Math.round(
                      rows.reduce(
                          (s, r) =>
                              s + r.driveThruTotalTimeSec * r.driveThruTransactions,
                          0
                      ) / totalTrans
                  )
                : 0,
        menuTimeSec:
            totalTrans > 0
                ? Math.round(
                      rows.reduce(
                          (s, r) => s + r.menuTimeSec * r.driveThruTransactions,
                          0
                      ) / totalTrans
                  )
                : 0,
        queueTimeSec:
            totalTrans > 0
                ? Math.round(
                      rows.reduce(
                          (s, r) => s + r.queueTimeSec * r.driveThruTransactions,
                          0
                      ) / totalTrans
                  )
                : 0,
        serviceTimeSec:
            totalTrans > 0
                ? Math.round(
                      rows.reduce(
                          (s, r) => s + r.serviceTimeSec * r.driveThruTransactions,
                          0
                      ) / totalTrans
                  )
                : 0
    };
}

function avgSeconds(
    rows: ISosBusinessImpactHourlyData[],
    getter: (r: ISosBusinessImpactHourlyData) => number
): number {
    if (rows.length === 0) return 0;
    const totalTrans = rows.reduce((s, r) => s + r.driveThruTransactions, 0);
    if (totalTrans === 0) return 0;
    return (
        rows.reduce((s, r) => s + getter(r) * r.driveThruTransactions, 0) /
        totalTrans
    );
}

const SosBusinessImpactModal: FC<IProps> = ({
    open,
    onClose,
    locationPk,
    businessDate,
    timeframeStartHour,
    timeframeEndHour
}) => {
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.Loading);
    const [data, setData] = useState<ISosBusinessImpactResponse | null>(null);
    const [selectedHours, setSelectedHours] = useState<Set<number>>(new Set());
    const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(
        ComparisonMode.Threshold
    );

    const screenWidth = useSelector(
        (state: AppState) => state.application.screenWidth
    );

    useEffect(() => {
        if (!open) return;

        const abortController = new AbortController();
        setDataStatus(DataStatus.Loading);
        setSelectedHours(new Set());
        setComparisonMode(ComparisonMode.Threshold);

        const fetchData = async () => {
            try {
                const response = await coachInsightsAPI.getSosBusinessImpact(
                    locationPk,
                    businessDate,
                    timeframeStartHour,
                    timeframeEndHour,
                    abortController.signal
                );
                setData(response);
                setDataStatus(DataStatus.Loaded);
            } catch (error: any) {
                if (error?.name !== "AbortError") {
                    setDataStatus(DataStatus.Failed);
                }
            }
        };

        fetchData();
        return () => abortController.abort();
    }, [open, locationPk, businessDate, timeframeStartHour, timeframeEndHour]);

    const hours = useMemo(() => {
        if (!data) return [];
        return data.hourlyData.map((h) => h.hour).sort((a, b) => a - b);
    }, [data]);

    const filteredData = useMemo(() => {
        if (!data) return [];
        if (selectedHours.size === 0) return data.hourlyData;
        return data.hourlyData.filter((h) => selectedHours.has(h.hour));
    }, [data, selectedHours]);

    const comparisonData = useMemo(() => {
        if (!data || comparisonMode === ComparisonMode.Threshold) return [];
        const source =
            comparisonMode === ComparisonMode.Wow
                ? data.wowHourlyData
                : data.yoyHourlyData;
        if (selectedHours.size === 0) return source;
        return source.filter((h) => selectedHours.has(h.hour));
    }, [data, comparisonMode, selectedHours]);

    const customColumns = useMemo(() => {
        if (!data) return {};
        return buildCustomColumns(
            data.thresholdSeconds,
            data.menuThresholdSeconds,
            data.queueThresholdSeconds,
            data.serviceThresholdSeconds,
            configurableColumns
        );
    }, [data]);

    const totalRow = useMemo(() => computeTotalRow(filteredData), [filteredData]);

    const tableData = useMemo<IDataTableData[]>(() => {
        const rows = filteredData.map((h) => ({
            hour: formatHourRange(h.hour),
            driveThruSales: h.driveThruSales,
            driveThruTransactions: h.driveThruTransactions,
            carsOverThreshold: h.carsOverThreshold,
            total: h.driveThruTotalTimeSec,
            menu: h.menuTimeSec,
            queue: h.queueTimeSec,
            service: h.serviceTimeSec
        }));

        return dataTableUtil.mapDataTableData(rows);
    }, [filteredData]);

    const tableFooterData = useMemo<IDataTableData[]>(() => {
        if (!totalRow || filteredData.length <= 1) return [];
        return dataTableUtil.mapDataTableData([
            {
                hour: "TOTAL",
                driveThruSales: totalRow.driveThruSales,
                driveThruTransactions: totalRow.driveThruTransactions,
                carsOverThreshold: totalRow.carsOverThreshold,
                total: totalRow.driveThruTotalTimeSec,
                menu: totalRow.menuTimeSec,
                queue: totalRow.queueTimeSec,
                service: totalRow.serviceTimeSec
            }
        ]);
    }, [filteredData, totalRow]);

    const metricCardData = useMemo(() => {
        const current = filteredData;
        const comparison = comparisonData;

        return {
            totalActual: avgSeconds(current, (r) => r.driveThruTotalTimeSec),
            totalComparison: avgSeconds(comparison, (r) => r.driveThruTotalTimeSec),
            menuActual: avgSeconds(current, (r) => r.menuTimeSec),
            menuComparison: avgSeconds(comparison, (r) => r.menuTimeSec),
            queueActual: avgSeconds(current, (r) => r.queueTimeSec),
            queueComparison: avgSeconds(comparison, (r) => r.queueTimeSec),
            serviceActual: avgSeconds(current, (r) => r.serviceTimeSec),
            serviceComparison: avgSeconds(comparison, (r) => r.serviceTimeSec)
        };
    }, [filteredData, comparisonData]);

    return (
        <Modal
            className="insights-modal business-impact-modal"
            open={open}
            onClose={onClose}
            closeIcon
            closeOnDimmerClick={true}
            size="large"
        >
            <Modal.Header>
                <div className="modal-header-content">
                    <div className="modal-header-left">
                        <Icon name="chart bar" />
                        <span className="modal-title">
                            Performance vs{" "}
                            {comparisonMode === ComparisonMode.Threshold
                                ? "Threshold"
                                : comparisonMode === ComparisonMode.Wow
                                ? "Last Week"
                                : "Last Year"}
                        </span>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Content scrolling>
                {dataStatus === DataStatus.Loading && <LoadingMessage />}
                {dataStatus === DataStatus.Failed && (
                    <ErrorMessage text="Failed to load business impact data." />
                )}
                {dataStatus === DataStatus.Loaded && data && (
                    <>
                        <div className="business-impact-hour-filter">
                            <span className="business-impact-filter-label">
                                Filter by Hour:
                            </span>
                            {hours.map((h) => (
                                <Button
                                    key={h}
                                    size="small"
                                    basic={!selectedHours.has(h)}
                                    primary={selectedHours.has(h)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedHours((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(h)) {
                                                next.delete(h);
                                            } else {
                                                next.add(h);
                                            }
                                            return next;
                                        });
                                    }}
                                >
                                    {formatHourRange(h)}
                                </Button>
                            ))}
                            {selectedHours.size > 0 && (
                                <Button
                                    size="small"
                                    basic
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedHours(new Set());
                                    }}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                        <br />
                        <div className="business-impact-comparison-toggle">
                            <Menu className="aggregationTab" compact>
                                <Menu.Item
                                    name="threshold"
                                    active={
                                        comparisonMode === ComparisonMode.Threshold
                                    }
                                    onClick={() =>
                                        setComparisonMode(ComparisonMode.Threshold)
                                    }
                                >
                                    Threshold
                                </Menu.Item>
                                <Menu.Item
                                    name="wow"
                                    active={comparisonMode === ComparisonMode.Wow}
                                    onClick={() =>
                                        setComparisonMode(ComparisonMode.Wow)
                                    }
                                >
                                    WOW
                                </Menu.Item>
                                <Menu.Item
                                    name="yoy"
                                    active={comparisonMode === ComparisonMode.Yoy}
                                    onClick={() =>
                                        setComparisonMode(ComparisonMode.Yoy)
                                    }
                                >
                                    YOY
                                </Menu.Item>
                            </Menu>
                        </div>

                        <div className="business-impact-metric-cards">
                            <MetricCard
                                label="DT SWS Total Time"
                                actualSeconds={metricCardData.totalActual}
                                thresholdSeconds={data.thresholdSeconds}
                                comparisonMode={comparisonMode}
                                comparisonSeconds={metricCardData.totalComparison}
                            />
                            <MetricCard
                                label="Menu Time"
                                actualSeconds={metricCardData.menuActual}
                                thresholdSeconds={data.menuThresholdSeconds}
                                comparisonMode={comparisonMode}
                                comparisonSeconds={metricCardData.menuComparison}
                            />
                            <MetricCard
                                label="Queue Time"
                                actualSeconds={metricCardData.queueActual}
                                thresholdSeconds={data.queueThresholdSeconds}
                                comparisonMode={comparisonMode}
                                comparisonSeconds={metricCardData.queueComparison}
                            />
                            <MetricCard
                                label="Window Time"
                                actualSeconds={metricCardData.serviceActual}
                                thresholdSeconds={data.serviceThresholdSeconds}
                                comparisonMode={comparisonMode}
                                comparisonSeconds={metricCardData.serviceComparison}
                            />
                        </div>
                        <div className="business-impact-data-grid">
                            <DataTable
                                staticColumns={staticColumns}
                                configurableColumns={configurableColumns}
                                data={tableData}
                                tableFooterData={
                                    tableFooterData.length > 0
                                        ? tableFooterData
                                        : undefined
                                }
                                sortColumnKey="hour"
                                itemsPerPage={25}
                                customColumns={customColumns}
                                screenWidth={screenWidth}
                            />
                        </div>
                    </>
                )}
            </Modal.Content>
        </Modal>
    );
};

export default SosBusinessImpactModal;
